import { useState, type FormEvent } from 'react';
import { siteInfo } from '@/mocks/home';

const SUBMIT_URL = 'https://readdy.ai/api/form/daimikjoh653ivfvogfg';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const topicOptions = ['素材投稿', '内容反馈 / 下架申请', '商务合作', '其他事项'];

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') params.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = /spam/i.test(String(serverMessage));

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMessage || '提交未成功，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络连接异常，请检查网络后重新提交。');
    }
  };

  return (
    <section id="contact" className="w-full bg-primary-950 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-sm font-label font-medium text-accent-300">
              <span className="w-6 h-[2px] bg-accent-400" aria-hidden="true"></span>
              联系我们
            </span>
            <h2 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-background-50 leading-snug">
              想投稿现场片段，或对内容有疑问？
            </h2>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-background-300">
              无论是投稿自己录到的现场素材，还是希望我们处理某条内容的版权与隐私问题，都可以通过右侧表单联系我们。编辑团队会在 24 小时内查看并回复。
            </p>

            <ul className="mt-8 flex flex-col gap-5">
              <li className="flex items-start gap-3.5">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-800 text-accent-300">
                  <i className="ri-mail-send-line text-xl" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-sm font-medium text-background-50">投稿专用邮箱</span>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="block mt-0.5 text-sm text-background-300 hover:text-accent-300 transition-colors break-all cursor-pointer"
                  >
                    {siteInfo.email}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-800 text-accent-300">
                  <i className="ri-time-line text-xl" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-sm font-medium text-background-50">处理时效</span>
                  <span className="block mt-0.5 text-sm text-background-300">
                    投稿审核 1–2 个工作日 · 异议处理 24 小时内
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-800 text-accent-300">
                  <i className="ri-file-list-3-line text-xl" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-sm font-medium text-background-50">投稿小贴士</span>
                  <span className="block mt-0.5 text-sm text-background-300">
                    说明素材拍摄时间、地点与视角，清晰度越高越容易被收录
                  </span>
                </span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-background-50 rounded-lg p-5 md:p-8">
              {status === 'success' ? (
                <div className="flex flex-col items-center text-center py-10">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-checkbox-circle-line text-4xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-5 font-heading font-bold text-xl text-foreground-950">
                    已收到你的信息
                  </h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                    感谢你的投稿与反馈，编辑团队会在 24 小时内查看。若需要补充素材细节，我们会通过你留下的邮箱与你联系。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line" aria-hidden="true"></i>
                    再提交一条
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  action={SUBMIT_URL}
                  method="post"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-name" className="text-sm font-label text-foreground-700">
                        称呼 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-email" className="text-sm font-label text-foreground-700">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="用于接收回复"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-topic" className="text-sm font-label text-foreground-700">
                      事项类型
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue={topicOptions[0]}
                      className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-colors cursor-pointer"
                    >
                      {topicOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-message" className="text-sm font-label text-foreground-700">
                      详细说明 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述素材内容、拍摄时间与大致视角，或说明内容反馈的具体情况（不超过 500 字）"
                      className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <span className="text-xs text-foreground-400">最多 500 字</span>
                  </div>

                  <div className="honey-field-holder">
                    <label htmlFor="contact-website-alt">请勿填写此栏</label>
                    <input
                      id="contact-website-alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {status === 'error' && formError && (
                    <p className="flex items-start gap-2 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-800">
                      <i className="ri-error-warning-line text-base mt-0.5" aria-hidden="true"></i>
                      <span>{formError}</span>
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-accent-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        正在提交…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill" aria-hidden="true"></i>
                        提交信息
                      </>
                    )}
                  </button>
                  <p className="text-xs leading-relaxed text-foreground-400">
                    提交即表示你同意我们仅将所填信息用于本次投稿与反馈处理，不会用于其他用途。
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}